import { useState, useEffect } from 'react';
import axios from 'axios';
import { useMMAuth } from '@machinemetrics/mm-react-tools';
import { useCurrentUser } from '../../hooks/useCurrentUser';

const API_BASE = '/machinesim/api';

const TEMPLATE_LABELS = {
  medical:    'Medical Devices',
  jobshop:    'Job Shop',
  automotive: 'Automotive',
};

const TEMPLATES = [
  {
    slug:        'medical',
    label:       'Medical Devices',
    description: 'High-precision small-batch production',
    machines:    '4× Swiss Lathe, 2× Mill, 2× Lathe',
  },
  {
    slug:        'jobshop',
    label:       'Job Shop',
    description: 'Mixed discrete manufacturing',
    machines:    '2× Lathe, 2× Mill, 1× Press, 1× Press Brake',
  },
  {
    slug:        'automotive',
    label:       'Automotive',
    description: 'High-volume stamping and machining',
    machines:    '4× Press, 2× Mill, 2× Lathe',
  },
];

function slugify(name) {
  return name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

const drawerInputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(168,144,96,0.2)',
  borderRadius: 4,
  color: 'var(--cn-text)',
  fontSize: 13,
  padding: '8px 12px',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
};

const drawerLabelStyle = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--cn-dimmed)',
  marginBottom: 5,
  display: 'block',
};

function chip(label, color) {
  const colors = {
    gold:    { bg: 'rgba(168,144,96,0.15)',  text: 'var(--cn-gold)',    border: 'rgba(168,144,96,0.35)' },
    green:   { bg: 'rgba(46,125,94,0.12)',   text: 'var(--cn-success)', border: 'rgba(46,125,94,0.3)'  },
    muted:   { bg: 'rgba(255,255,255,0.04)', text: 'var(--cn-dimmed)',  border: 'rgba(255,255,255,0.1)' },
  };
  const c = colors[color] || colors.muted;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
      padding: '2px 8px', borderRadius: 999,
      border: `1px solid ${c.border}`, color: c.text, background: c.bg,
      display: 'inline-block', marginLeft: 6,
    }}>
      {label}
    </span>
  );
}

export default function CompaniesPage() {
  const [companies, setCompanies]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [publishTarget, setPublishTarget] = useState(null); // co to publish
  const [publishing, setPublishing]   = useState(false);
  const [authError, setAuthError]     = useState('');

  const auth        = useMMAuth();
  const accessToken = auth?.accessToken ?? null;
  const { user: currentUser } = useCurrentUser();

  const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

  const load = async () => {
    try {
      const res = await axios.get(`${API_BASE}/companies`, { headers: authHeaders });
      setCompanies(res.data.companies || []);
    } catch (err) {
      if (err.response?.status === 401) setAuthError('Session expired. Please log in again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmPublish = async () => {
    if (!publishTarget) return;
    setPublishing(true);
    setAuthError('');
    try {
      await axios.patch(
        `${API_BASE}/companies/${publishTarget.slug}`,
        { isPublic: true },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } },
      );
      setPublishTarget(null);
      await load();
    } catch (err) {
      if (err.response?.status === 401) setAuthError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setAuthError('Access denied. Only the company owner can publish.');
      else setAuthError(err.response?.data?.error || 'Failed to publish company.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100%' }}>
      {/* Publish confirmation modal */}
      {publishTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.2)', borderRadius: 8, padding: '28px 32px', maxWidth: 420, width: '90%' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--cn-text)', marginBottom: 12 }}>Publish Company?</div>
            <div style={{ fontSize: 13, color: 'var(--cn-muted)', lineHeight: 1.6, marginBottom: 20 }}>
              Once published, <strong style={{ color: 'var(--cn-text)' }}>{publishTarget.name}</strong> is visible to all users in your organization and cannot be made private again.
            </div>
            {authError && <div style={{ fontSize: 12, color: 'var(--cn-danger)', marginBottom: 12 }}>{authError}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setPublishTarget(null); setAuthError(''); }}
                style={{ padding: '8px 16px', borderRadius: 4, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', background: 'rgba(168,144,96,0.1)', color: 'var(--cn-gold)', border: '1px solid rgba(168,144,96,0.25)' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmPublish}
                disabled={publishing}
                style={{ padding: '8px 16px', borderRadius: 4, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: publishing ? 'not-allowed' : 'pointer', background: 'var(--cn-gold)', color: '#0D1B2A', border: 'none', opacity: publishing ? 0.6 : 1 }}
              >
                {publishing ? 'Publishing…' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        padding: '32px 36px',
        marginRight: drawerOpen ? 440 : 0,
        transition: 'margin-right 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--cn-text)', margin: 0, flex: 1 }}>
            Companies
          </h1>
          <button
            onClick={() => setDrawerOpen(true)}
            style={{
              padding: '8px 16px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderRadius: 4,
              border: '1px solid rgba(168,144,96,0.25)',
              background: 'rgba(168,144,96,0.1)',
              color: 'var(--cn-gold)',
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(168,144,96,0.18)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(168,144,96,0.1)'}
          >
            + New Company
          </button>
        </div>

        {authError && !publishTarget && (
          <div style={{ fontSize: 12, color: 'var(--cn-danger)', marginBottom: 16, padding: '8px 12px', background: 'rgba(192,57,43,0.1)', borderRadius: 4, border: '1px solid rgba(192,57,43,0.2)' }}>
            {authError}
          </div>
        )}

        {/* Company table */}
        {loading ? (
          <div style={{ color: 'var(--cn-muted)', padding: 24 }}>Loading…</div>
        ) : (
          <div style={{
            background: 'var(--cn-surface)',
            border: '1px solid rgba(168,144,96,0.1)',
            borderRadius: 8,
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--cn-surface2)', borderBottom: '1px solid rgba(168,144,96,0.15)' }}>
                  {['Name', 'Template', 'Machines', 'Status', ''].map((h, i) => (
                    <th key={i} style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'var(--cn-muted)',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {companies.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--cn-dimmed)' }}>
                      {/* Empty state is the literal first-run experience — give it the single
                          obvious next action instead of a dead end (Von Restorff / Peak-End). */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <span>No companies yet.</span>
                        <button onClick={() => setDrawerOpen(true)}
                          style={{ padding: '8px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', borderRadius: 4, border: 'none', background: 'var(--cn-gold)', color: '#0D1B2A', cursor: 'pointer' }}>
                          + New Company
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  companies.map((co, idx) => {
                    const isLast   = idx === companies.length - 1;
                    const isOwner  = currentUser && co.ownerEmail === currentUser.email;
                    const canPublish = isOwner && !co.isPublic;
                    return (
                      <tr
                        key={co.id}
                        style={{ borderBottom: !isLast ? '1px solid rgba(168,144,96,0.06)' : 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '11px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                            <span style={{ fontWeight: 600, color: 'var(--cn-text)', fontSize: 13 }}>{co.name}</span>
                            {isOwner  && chip('Yours', 'gold')}
                            {co.isPublic && chip('Public', 'green')}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--cn-dimmed)', marginTop: 2, fontFamily: '"Courier New", monospace' }}>{co.slug}</div>
                        </td>
                        <td style={{ padding: '11px 16px', color: 'var(--cn-muted)', fontSize: 12 }}>
                          {TEMPLATE_LABELS[co.templateSlug] ?? 'Default'}
                        </td>
                        <td style={{ padding: '11px 16px', color: 'var(--cn-text)', fontSize: 12 }}>
                          {co.machineCount ?? 0}
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          {co.running ? (
                            <span style={{
                              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                              padding: '3px 8px', borderRadius: 999,
                              border: '1px solid var(--cn-success)',
                              color: 'var(--cn-success)',
                              background: 'rgba(46,125,94,0.1)',
                              textTransform: 'uppercase',
                            }}>
                              Running
                            </span>
                          ) : (
                            <span style={{
                              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                              padding: '3px 8px', borderRadius: 999,
                              border: '1px solid rgba(255,255,255,0.1)',
                              color: 'var(--cn-dimmed)',
                              background: 'rgba(255,255,255,0.04)',
                              textTransform: 'uppercase',
                            }}>
                              Stopped
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                          {canPublish && (
                            <button
                              onClick={() => { setAuthError(''); setPublishTarget(co); }}
                              title="Publish makes this company public — this cannot be undone"
                              style={{
                                padding: '4px 12px', borderRadius: 4, fontSize: 11,
                                fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                                cursor: 'pointer', background: 'rgba(199,138,58,0.12)',
                                color: 'var(--cn-warning)', border: '1px solid rgba(199,138,58,0.35)',
                              }}
                            >
                              Publish
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer */}
      <CompanyDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={() => { setDrawerOpen(false); load(); }}
      />
    </div>
  );
}

function CompanyDrawer({ open, onClose, onCreated }) {
  const [name, setName]                   = useState('');
  // Pre-select the most general template so there's a sensible default and the only
  // required step is naming (Default bias / Goal-Gradient — reduce friction on the common path).
  const [templateSlug, setTemplateSlug]   = useState('jobshop');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');

  const auth = useMMAuth();
  const accessToken = (auth && typeof auth === 'object') ? (auth.accessToken ?? null) : null;

  // Reset when drawer opens
  useEffect(() => {
    if (open) {
      setName('');
      setTemplateSlug('jobshop');
      setError('');
      setSaving(false);
    }
  }, [open]);

  const slug = slugify(name);
  const canSubmit = !saving && name.trim().length > 0 && templateSlug !== '';

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      await axios.post(`${API_BASE}/companies`, {
        name: name.trim(),
        templateSlug,
      }, { headers });
      onCreated();
    } catch (err) {
      if (err.response?.status === 401) setError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setError('Access denied.');
      else setError(err.response?.data?.error || 'Failed to create company');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      right: 0,
      top: 0,
      height: '100vh',
      width: 440,
      background: 'var(--cn-surface)',
      borderLeft: '1px solid rgba(168,144,96,0.15)',
      boxShadow: '-12px 0 40px rgba(0,0,0,0.35)',
      transform: open ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
      zIndex: 50,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Collapse tab */}
      <button
        onClick={onClose}
        title="Collapse"
        style={{
          position: 'absolute',
          left: -30,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 30,
          height: 80,
          background: 'var(--cn-surface)',
          border: '1px solid rgba(168,144,96,0.15)',
          borderRight: 'none',
          borderRadius: '6px 0 0 6px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--cn-muted)',
          fontSize: 16,
          boxShadow: '-4px 0 12px rgba(0,0,0,0.2)',
          transition: 'color 0.15s, background 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(168,144,96,0.1)';
          e.currentTarget.style.color = 'var(--cn-gold)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--cn-surface)';
          e.currentTarget.style.color = 'var(--cn-muted)';
        }}
      >
        ›
      </button>

      {/* Drawer header */}
      <div style={{
        padding: '20px 20px 16px',
        borderBottom: '1px solid rgba(168,144,96,0.1)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--cn-text)' }}>New Company</div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--cn-dimmed)', cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cn-text)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--cn-dimmed)'}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Drawer body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Name input */}
          <div>
            <label style={drawerLabelStyle}>Company Name</label>
            <input
              type="text"
              placeholder="e.g. Med Factory Alpha"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={drawerInputStyle}
            />
            {slug && (
              <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginTop: 5, fontFamily: '"Courier New", monospace' }}>
                slug: {slug}
              </div>
            )}
          </div>

          {/* Template picker */}
          <div>
            <label style={drawerLabelStyle}>Template</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {TEMPLATES.map((tpl) => {
                const isSelected = templateSlug === tpl.slug;
                return (
                  <div
                    key={tpl.slug}
                    onClick={() => setTemplateSlug(tpl.slug)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: 5,
                      border: isSelected
                        ? '1px solid var(--cn-gold)'
                        : '1px solid rgba(168,144,96,0.15)',
                      background: isSelected ? 'rgba(168,144,96,0.1)' : 'rgba(255,255,255,0.02)',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                    }}
                  >
                    {/* Radio dot */}
                    <div style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      flexShrink: 0,
                      marginTop: 2,
                      border: isSelected ? '4px solid var(--cn-gold)' : '2px solid rgba(168,144,96,0.4)',
                      background: isSelected ? 'var(--cn-gold)' : 'transparent',
                      transition: 'all 0.15s',
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? 'var(--cn-gold)' : 'var(--cn-text)' }}>
                          {tpl.label}
                        </span>
                        {tpl.slug === 'jobshop' && chip('Recommended', 'gold')}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--cn-muted)', marginBottom: 3 }}>
                        {tpl.description}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--cn-dimmed)', letterSpacing: '0.03em' }}>
                        {tpl.machines}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              fontSize: 12,
              color: 'var(--cn-danger)',
              padding: '8px 12px',
              background: 'rgba(192,57,43,0.1)',
              borderRadius: 4,
              border: '1px solid rgba(192,57,43,0.2)',
            }}>
              {error}
            </div>
          )}

          {/* Why the primary is disabled, instead of a silent greyed button (Cognitive Load). */}
          {!saving && !name.trim() && (
            <div style={{ fontSize: 11, color: 'var(--cn-dimmed)' }}>Enter a company name to continue.</div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 4, fontSize: 12,
                fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                cursor: 'pointer', background: 'rgba(168,144,96,0.1)',
                color: 'var(--cn-gold)', border: '1px solid rgba(168,144,96,0.25)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              style={{
                flex: 2, padding: '9px 0', borderRadius: 4, fontSize: 12,
                fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                background: 'var(--cn-gold)', color: '#0D1B2A', border: 'none',
                opacity: canSubmit ? 1 : 0.45,
                transition: 'opacity 0.15s',
              }}
            >
              {saving ? 'Creating…' : 'Create Company'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
